import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import useFullScreen from '../../hooks/useFullScreen';
import MarkdownWithMermaid from '../common/MarkdownWithMermaid';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import SectionHeader from '../atoms/Header';
import TopBarButtons from '../atoms/TopBarButtons';
import LoadMoreButton from '../atoms/LoadMore';
import ConfirmationDialog from '../common/ConfirmationDialog';
import useDynamicSections from '../../hooks/useDynamicSections';
import useSectionCRUDV1 from '../../hooks/useSectionCRUDV1';
import useContentActions from '../../hooks/useContentActions';
import useNotification from '../../hooks/useNotification';
import EmptyContentPlaceholder from '../atoms/EmptyContentPlaceholder';
import { Accordion, ScrollableBox } from '../atoms';
import IsolatedEditSection from '../atoms/IsolatedEditSection';
import useExplain from '../../hooks/useExplain';
import ExplanationModal from '../atoms/ExplanationModal';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import ImprovementModal from '../atoms/ImprovementModal';
import useArchitectureVersions from '../../hooks/useArchitectureVersions';
import VersionSelector from '../atoms/VersionSelector';
import conversationService from '../../services/conversationService';
import AddSectionModal from '../atoms/AddSectionModal';
import LoadingBox from '../atoms/LoadingBox';
import useParsedSections from '../../hooks/useParsedSections';

/**
 * A split-view layout component that shows conversation on the left
 * and dynamically rendered architecture design on the right with accordion sections
 */
const SplitViewArchitecture = ({ conversation, children, isStreaming, streamingContent, projectId }) => {
  // Determine conversation types first
  const isArchitectureConversation = conversation && (conversation?.taskId || conversation?.task_id) === 'architecture';
  const isApiGuidelinesConversation = conversation && (conversation?.taskId || conversation?.task_id) === 'api-guidelines';
  const shouldShowSplitView = isArchitectureConversation || isApiGuidelinesConversation;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // Default zoom to 1.5x for better diagram visibility
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const { showNotification } = useNotification();
  const architecturePanelRef = useRef(null);

  // Use version management for architecture sections
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useArchitectureVersions(conversation, isStreaming, streamingContent);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  const [expandedSections, setExpandedSections] = useState({});

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);
  const [visibleSections, setVisibleSections] = useState(20);
  const isLocalUpdateRef = useRef(false); // Track if we're doing a local update
  const editSectionRef = useRef(null); // Ref to access IsolatedEditSection's save method

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      const versionChanged = prevVersionIndexRef.current !== currentVersionIndex;
      if (versionChanged) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  const hasContent = parsedSections.length > 0;
  const showArchitecturePanel = parsedSections.length > 0;

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, parsedSections.length));
  };

  const handleShowAll = () => {
    setVisibleSections(parsedSections.length);
  };

  // Use CRUD operations hook (V1 - optimized for typing performance)
  const {
    editingSection,
    editedTitle,
    setEditedTitle,
    isSaving,
    deleteConfirmation,
    setDeleteConfirmation,
    handleStartEditV1,
    handleCancelEdit,
    handleSaveEditWithContent,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleImproveSection,
    handleExplainSection,
    handleCopySection
  } = useSectionCRUDV1(conversation, parsedSections, setParsedSections, setExpandedSections, contentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, null, markLocalEdit);

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport,
    handleCopy,
    handleRefreshDiagrams,
    handleSaveToDocument,
    getExportContent,
  } = useContentActions(contentRef, 'Architecture Design', projectId, conversation?._id);

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Create wrapper for explain that uses the new hook
  const handleExplainSectionWrapper = (section) => {
    requestExplanation(section);
  };

  // Improve functionality
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Handler for applying improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;
    try {
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        return;
      }
      closeImprovementModal();
      showNotification({ message: 'Section improved successfully', type: 'success' });
    } catch (error) {
      showNotification({ message: 'Failed to apply improvement', type: 'error' });
    }
  };

  // Handler to trigger save from ActionButtons using the ref
  const handleSaveFromActionButtons = (sectionId) => {
    if (editSectionRef.current && editSectionRef.current.save) {
      editSectionRef.current.save();
    }
  };

  // Full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state for sections with version-specific IDs
  const initialValidationState = parsedSections.reduce((acc, section) => {
    // Create version-specific validation ID
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'architecture',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'architecture',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    // Create version-specific ID
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  console.log('SplitViewArchitecture rendering', {
    conversationId: conversation?._id,
    taskId: conversation?.taskId,
    isArchitectureConversation,
    isApiGuidelinesConversation,
    shouldShowSplitView,
    isStreaming
  });

  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && architecturePanelRef.current && isArchitectureConversation) {
      setTimeout(() => {
        if (architecturePanelRef.current) {
          architecturePanelRef.current.scrollTop = architecturePanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [isStreaming, isArchitectureConversation]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 2.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.25));
  };

  // Handler for adding new architecture section
  const handleAddNewSection = async (newSectionData) => {
    try {
      markLocalEdit();
      isLocalUpdateRef.current = true;

      // Generate next section number
      const nextNumber = parsedSections.length + 1;
      const sectionId = `architecture-${nextNumber}`;

      // Create new section object
      const newSection = {
        id: sectionId,
        title: newSectionData.title,
        content: newSectionData.content,
        rawContent: newSectionData.content
      };

      // Add to sections array
      const updatedSections = [...parsedSections, newSection];
      setParsedSections(updatedSections);

      // Update expanded state to show new section
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: true
      }));

      // Reconstruct full markdown content
      const updatedContent = updatedSections.map((section, index) =>
        `## ${section.title}\n\n${section.content}`
      ).join('\n\n');

      // Update content ref
      contentRef.current = updatedContent;

      // Update the current version if available
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent
        });
      }

      // Save to backend - update the last assistant message
      if (conversation && conversation._id && conversation.messages) {
        // Find the last assistant message and update its content
        const updatedMessages = conversation.messages.map((msg, index) => {
          // Update the last assistant message
          if (msg.role === 'assistant' && index === conversation.messages.length - 1) {
            return {
              ...msg,
              content: updatedContent,
              createdAt: msg.createdAt || msg.created_at
            };
          }
          return {
            ...msg,
            createdAt: msg.createdAt || msg.created_at
          };
        });

        await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/conversations/${conversation._id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              messages: updatedMessages
            })
          }
        );
      }

      showNotification({
        message: `Section "${newSectionData.title}" added successfully`,
        type: 'success'
      });

      setShowAddSectionModal(false);

      // Reset the flag after a delay
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 1000);

    } catch (error) {
      console.error('Error adding section:', error);
      showNotification({
        message: 'Failed to add section',
        type: 'error'
      });
      isLocalUpdateRef.current = false;
    }
  };

  // Helper function to check if content has Mermaid diagram
  const hasMermaidDiagram = (content) => {
    return content && content.includes('```mermaid');
  };

  // Don't show the split view if this isn't an architecture conversation
  // or if no architecture sections are found and we're not streaming
  if (!shouldShowSplitView || (!showArchitecturePanel && !isStreaming)) {
    console.log('Not showing split view:', {
      shouldShowSplitView,
      showArchitecturePanel,
      hasVersions,
      isStreaming,
      parsedSectionsCount: parsedSections.length
    });
    return <>{children}</>;
  }

  console.log('Showing split view with architecture content', {
    hasContent,
    hasVersions,
    showArchitecturePanel,
    isStreaming,
    parsedSectionsCount: parsedSections.length
  });

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Architecture panel */}
        <Box
          ref={architecturePanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              p: 3,
              pb: 2,
            }}>
              <SectionHeader label='Architecture design' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={setCurrentVersionIndex}
                    disabled={isStreaming}
                  />
                )}
                {/* Fixed header section */}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddSectionModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExport}
                  isExporting={isExporting}
                  onCopy={handleCopy}
                  onRefresh={handleRefreshDiagrams}
                  onSave={projectId ? () => handleSaveToDocument('ARCHITECTURE') : undefined}
                  isSaving={isSaving || isSavingDocument}
                  disabled={!contentRef.current}
                  addTooltip="Add new architecture section"
                  exportTooltip="Export architecture as markdown"
                  copyTooltip="Copy architecture to clipboard"
                  refreshTooltip="Refresh diagrams"
                  saveTooltip="Save as document"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'architecture'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show architecture content or a placeholder if none found yet */}
              {isLoadingVersion ? (

                <LoadingBox message={`Loading version ${currentVersionIndex + 1}...`} spinnerSize={40} />

              ) : isStreaming ? (
                <LoadingBox message="Generating architecture design..." spinnerSize={40} />

              ) : showArchitecturePanel && parsedSections.length > 0 ? (
                <Box>
                  {/* Group sections by category for hierarchical display */}
                  {(() => {
                    // Build grouped structure: [{category, categorySections (content-bearing), sections}]
                    const groups = [];
                    let currentGroup = null;
                    parsedSections.forEach((section) => {
                      if (section.isCategory || section.isFirstInCategory || !currentGroup) {
                        currentGroup = {
                          category: section.category || section.title,
                          categoryId: section.isCategory ? section.id : `cat-${section.id}`,
                          // If isCategory section has real content, include it as a section
                          // but use the category name from it (avoid duplicate header)
                          sections: section.isCategory ? [] : [section],
                          // Store the category section separately if it has content
                          categorySection: section.isCategory && section.content ? section : null,
                        };
                        groups.push(currentGroup);
                      } else {
                        currentGroup.sections.push(section);
                      }
                    });

                    let globalIndex = 0;
                    return groups.slice(0, visibleSections).map((group, groupIndex) => (
                      <Box key={group.categoryId} sx={{
                        mb: 4,
                        pb: 2,
                        borderBottom: groupIndex < groups.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}>
                        {/* Category Header */}
                        <Box sx={{ mt: groupIndex === 0 ? 0 : 1, mb: 2 }}>
                          <SectionHeader label={group.category} />
                        </Box>

                        {/* Category intro content — clean metadata block, copy only */}
                        {group.categorySection && (
                          <Box sx={{ pl: 2, mb: 2, position: 'relative' }}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: 'rgba(95, 138, 143, 0.04)',
                              borderRadius: 1,
                              borderLeft: '3px solid #5f8a8f',
                              fontSize: '13px',
                              '& p': { mb: 0.5, '&:last-child': { mb: 0 } },
                              '& strong': { color: '#3A4754', display: 'block', mt: 0.5 },
                              '& li': { mb: 0.25 },
                            }}>
                              <MarkdownWithMermaid content={
                                // Force each **Label:** to its own line
                                (group.categorySection.content || '')
                                  .replace(/\*\*([A-Z][^*]+):\*\*/g, '\n**$1:**')
                                  .trim()
                              } zoomLevel={zoomLevel} />
                            </Box>
                            <Box sx={{ position: 'absolute', right: 10, top: 10 }}>
                              <ActionButtons
                                item={group.categorySection}
                                itemIndex={globalIndex++}
                                featureId={group.categorySection.id}
                                editingFeature={null}
                                isSaving={false}
                                onCopy={() => handleCopySection(group.categorySection)}
                                vertical={true}
                          onComment={() => openCommentPanel(group.categorySection.id)}
                          commentCount={commentCounts[group.categorySection.id]?.open || 0}
                          isApproved={!!validatedSections[`${group.categorySection.id}_vt${currentVersion?.timestamp || currentVersionIndex}`]}
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Child sections within this category */}
                        <Box sx={{ pl: 2 }}>
                          {group.sections.map((section) => {
                            const idx = globalIndex++;
                            const sectionId = section.id;
                            const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                            const isValidated = validatedSections[versionSpecificId] || false;

                            return (
                              <React.Fragment key={section.id}>
                                <Accordion
                                  ref={shouldHighlight(section.id) ? highlightRef : null}
                                  expanded={expandedSections[section.id] || false}
                                  onChange={() => handleSectionToggle(section.id)}
                                  expandIcon={false}
                                  sx={{
                                    mb: 1.5,
                                    '&.Mui-expanded': { margin: 0, mb: 1.5 },
                                  }}
                                  summaryContent={
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                      justifyContent: 'space-between'
                                    }}>
                                      {editingSection === section.id ? (
                                        <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                                          <TextField
                                            value={editedTitle}
                                            onChange={(e) => setEditedTitle(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.target.removeAttribute('title')}
                                            size="small"
                                            fullWidth
                                            placeholder="Enter section title"
                                            variant="outlined"
                                            InputProps={{ title: '' }}
                                            inputProps={{ title: '', autoComplete: 'off' }}
                                            sx={{
                                              '& .MuiOutlinedInput-root': {
                                                fontSize: '14px',
                                                fontFamily: 'inherit',
                                                backgroundColor: '#fff',
                                                '& fieldset': { borderWidth: '1px' },
                                                '&:hover fieldset': { borderWidth: '1px' },
                                                '&.Mui-focused fieldset': { borderWidth: '1px' },
                                              },
                                              '& .MuiOutlinedInput-input': {
                                                fontSize: '14px',
                                                fontFamily: 'inherit',
                                                padding: '8px 12px',
                                              },
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "15px", textTransform: 'none !important' }}>
                                          {section.title}
                                        </Typography>
                                      )}
                                      <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: "column",
                                        justifyContent: "center"
                                      }}>
                                        <Box onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            await handleValidateSection(section.id);
                                          } catch (error) {
                                            showNotification({
                                              message: `Failed to update validation for "${section.title}"`,
                                              type: "error"
                                            });
                                          }
                                        }}
                                          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}>
                                          {isValidated ? (
                                            <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                                          ) : (
                                            <UnvalidatedIcon size={25} />
                                          )}
                                        </Box>
                                        <Box sx={{
                                          display: 'flex',
                                          transform: expandedSections[section.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                          transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                                        }}>
                                          <ExpandMoreIcon fontSize="small" />
                                        </Box>
                                      </Box>
                                    </Box>
                                  }
                                  detailsSx={{
                                    cursor: editingSection === section.id ? 'default' : 'pointer'
                                  }}
                                  onDetailsClick={() => {
                                    if (!editingSection && editingSection !== section.id) {
                                      handleStartEditV1(section.id, section.title || '');
                                    }
                                  }}
                                >
                                  <ScrollableBox
                                    minHeight="300px"
                                    sx={{
                                      textAlign: 'justify',
                                      cursor: editingSection === section.id ? 'default' : 'auto',
                                    }}
                                  >
                                    {editingSection === section.id ? (
                                      <IsolatedEditSection
                                        ref={editSectionRef}
                                        initialContent={section.content || ''}
                                        onSave={(content) => handleSaveEditWithContent(section.id, content)}
                                        onCancel={handleCancelEdit}
                                        isSaving={isSaving}
                                        showButtons={false}
                                      />
                                    ) : (
                                      <Box ref={getSectionContentRef(section.id)}>
                                        <MarkdownWithMermaid content={section.content} zoomLevel={zoomLevel} />
                                      </Box>
                                    )}
                                    <TextSelectionPopup
                                      containerRef={getSectionContentRef(section.id)}
                                      sectionId={section.id}
                                      onSubmitComment={addComment}
                                      disabled={editingSection === section.id}
                                    />
                                  </ScrollableBox>
                                  <Box sx={{
                                    position: 'absolute',
                                    right: 10,
                                    top: 10,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5
                                  }}>
                                    <ActionButtons
                                      item={section}
                                      itemIndex={idx}
                                      featureId={section.id}
                                      editingFeature={editingSection}
                                      isSaving={isSaving}
                                      onEdit={() => handleStartEditV1(section.id, section.title || '')}
                                      onSave={() => handleSaveFromActionButtons(section.id)}
                                      onCancel={handleCancelEdit}
                                      onImprove={() => handleImproveClick(section)}
                                      onExplain={() => handleExplainSectionWrapper(section)}
                                      onCopy={() => handleCopySection(section)}
                                      onDelete={() => handleDeleteSection(section)}
                                      onZoomIn={handleZoomIn}
                                      onZoomOut={handleZoomOut}
                                      showZoomControls={hasMermaidDiagram(section.content || section.rawContent || '')}
                                      zoomLevel={zoomLevel}
                                      minZoom={0.25}
                                      maxZoom={2.0}
                                      vertical={true}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                                    />
                                  </Box>
                                </Accordion>
                                {showImproveInput === section.id && (
                                  <InlineImproveInput
                                    improvePrompt={improvePrompt}
                                    onPromptChange={setImprovePrompt}
                                    onSend={() => handleSendImprove(section)}
                                    onCancel={handleCancelImprove}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    ));
                  })()}

                  {/* Load more button */}
                  {(() => {
                    // Count groups for load-more (visibleSections controls groups, not individual sections)
                    const groups = [];
                    let cg = null;
                    parsedSections.forEach(s => {
                      if (s.isCategory || s.isFirstInCategory || !cg) { cg = { sections: [s] }; groups.push(cg); }
                      else { cg.sections.push(s); }
                    });
                    return groups.length > visibleSections ? (
                      <LoadMoreButton
                        remaining={groups.length - visibleSections}
                        onLoadMore={handleLoadMore}
                        onShowAll={handleShowAll}
                        label="Load more sections"
                      />
                    ) : null;
                  })()}
                </Box>
              ) : (
                <EmptyContentPlaceholder
                  title="No architecture design found yet."
                  description="Ask the AI to design a software architecture following SOLID principles and using appropriate design patterns. You can use prompts like:"
                  examplePrompt="Can you create an architecture design for my application following SOLID principles and recommend appropriate design patterns?"
                  additionalInfo="You can also ask for specific diagrams like component architecture, sequence diagrams, or database schema."
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Confirmation dialog for delete */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        onConfirm={handleDeleteSectionConfirmed}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height='auto'
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        onSave={handleAddNewSection}
        sectionType="architecture"
        existingSections={parsedSections}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        onUpdate={handleUpdateImprovedContent}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewArchitecture;
